import { useCallback } from 'react';

/**
 * Hook for managing ACTIVE section highlights (persistent yellow boxes).
 *
 * Active highlights are persistent visual indicators (yellow boxes) that remain
 * visible until dismissed. They are triggered by:
 * - URL hash navigation (e.g., #section-id from citation links)
 * - User clicks on sections
 *
 * This is separate from HOVER highlights (transient outlines) which are handled
 * by native event listeners in html-link-handler.tsx for instant visual feedback.
 *
 * This is a thin wrapper that works with the unified active highlight system
 * provided by useActiveHighlight hook.
 *
 * @param addActiveHighlight - Function from useActiveHighlight hook to add highlights
 * @returns Object with highlightAndScrollToSection function
 */
export const useActiveHighlightSection = (
  addActiveHighlight: (elementId: string, type: 'hash' | 'click') => void
) => {
  const highlightAndScrollToSection = useCallback(
    (hash: string) => {
      if (!hash) return;

      // Try to find the element by ID
      let sectionElement = document.getElementById(hash);

      // Fallback: try with CSS.escape for special characters
      if (!sectionElement) {
        const escapedSelector = CSS.escape(hash);
        const elements = document.querySelectorAll(`#${escapedSelector}`);
        if (elements.length > 0) {
          sectionElement = elements[0] as HTMLElement;
        }
      }

      if (sectionElement) {
        // Use the unified active highlight system
        addActiveHighlight(hash, 'hash');
      }
    },
    [addActiveHighlight]
  );

  return { highlightAndScrollToSection };
};
